<template>
  <div class="assignments-actions">
    <BulkActionDropdown
      :menu-items="[
        {
          icon: 'check',
          label: this.$t('assignments.listing.labels.approveAssignments'),
          clickaction: 'APPROVE'
        },
        {
          icon: 'times',
          label: this.$t('assignments.listing.labels.rejectAssignments'),
          clickaction: 'REJECT'
        },
        { icon: 'copy', label: this.$t('assignments.listing.labels.bulkPdf'), clickaction: 'PDF' }
      ]"
      @bulk-action-selected="onBulkActionSelected($event)"
      @all-none-selected="selectOrDeselectAssignments($event)"
    />
    <div class="listing-title">{{ $t(titleAllOrFiltered) }}</div>

    <div class="search">
      <tol-type-ahead
        :display-value="assignmentsFilters.searchParam"
        :search-params="{
          url: '/assignments',
          searchKeys: ['searchParam'],
          urlParams: {
            recordsPerPage: assignmentsRecordsPerpage,
            offset: assignmentsRecordsOffset,
            sortBy: assignmentsSortField,
            sortDirection: assignmentsSortDirection,
            assignmentsFilters: assignmentsFilters
          }
        }"
        :results-in-dropdown="false"
        @filtered="onFiltered($event)"
        @search-in-progress="onSearchInProgress($event)"
      >
      </tol-type-ahead>
    </div>
    <router-link to="/assignments/create">
      <div class="create-action-container">
        <TolAltButton label="Create Assignment" />
      </div>
    </router-link>
    <ConfirmModal
      v-if="showConfirmModal"
      :title="selectedBulkAction"
      :action="selectedBulkAction"
      @continue-action="continueBulkAction($event)"
      @cancel-action="cancelBulkAction()"
    >
      <template v-slot:modalData>
        {{ $t('bulk.labels.totalSelected') }}: {{ selectedItems.length }}
      </template>
      <template v-slot:modalMessage>
        {{ $t('bulk.dialogs.approveRejectConfirmationDialog.confirmMessage', { message }) }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import TolAltButton from '@/app/components/TolAltButton';
import TolTypeAhead from '@/app/components/TolTypeAhead';
import {
  AssignmentsListingActions,
  AssignmentsListingGetters
} from '@/app/features/assignments/features/assignments-listing/assignments-listing.keys';
import BulkActionDropdown from '@/app/components/BulkActionDropdown.vue';
import ConfirmModal from '@/app/components/ConfirmModal.vue';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';

export default {
  name: 'AssignmentsActions',
  components: {
    TolAltButton,
    TolTypeAhead,
    ConfirmModal,
    BulkActionDropdown
  },
  props: {
    titleAllOrFiltered: {
      type: String,
      default: 'assignments.listing.actions.titleAll'
    },
    listingOptions: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showConfirmModal: false,
      selectedBulkAction: ''
    };
  },
  computed: {
    assignmentsRecordsPerpage() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsPerPage];
    },
    assignmentsRecordsOffset() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsListingOffset];
    },
    assignmentsSortField() {
      return this.$store.getters[AssignmentsListingGetters.SortBy];
    },
    assignmentsSortDirection() {
      return this.$store.getters[AssignmentsListingGetters.SortDirection];
    },
    assignmentsFilters() {
      return this.$store.getters[AssignmentsListingGetters.AssignmentsFilters];
    },
    anyItemsSelected() {
      return this.selectedItems.length > 0;
    },
    selectedItems() {
      return this.$store.getters[AssignmentsListingGetters.SelectedAssignments].map(
        assignment => assignment.referenceNumber
      );
    },
    message: {
      get() {
        switch (this.selectedBulkAction) {
          case 'APPROVE':
            return this.$t('bulk.actions.approve');
          case 'REJECT':
            return this.$t('bulk.actions.reject');
          case 'PDF':
            return this.$t('bulk.actions.saveAsPdf');
          default:
            return '';
        }
      }
    }
  },
  methods: {
    onBulkActionSelected(bulkAction) {
      this.selectedBulkAction = bulkAction;
      if (this.canDoBulkAction(bulkAction)) this.showConfirmModal = this.anyItemsSelected;
      else {
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('bulk.errors.approveRejectDisallowed')
        });
      }
      this.$store.dispatch(AssignmentsListingActions.SetAssignmentsBulkAction, bulkAction);
    },
    continueBulkAction(event) {
      this.showConfirmModal = false;
      this.$store.dispatch(AssignmentsListingActions.DoAssignmentsBulkAction, event);
    },
    cancelBulkAction() {
      this.showConfirmModal = false;
      this.$store.dispatch(AssignmentsListingActions.SelectDeselectAllAssignments, {
        clicked: false
      });
    },
    selectOrDeselectAssignments(event) {
      this.$store.dispatch(AssignmentsListingActions.SelectDeselectAllAssignments, event);
    },
    canDoBulkAction(bulkAction) {
      const selectedAssignmentStatuses = this.$store.getters[
        AssignmentsListingGetters.SelectedAssignments
      ].map(assignment => assignment.status);

      switch (bulkAction) {
        case 'APPROVE':
          return selectedAssignmentStatuses.every(
            status => status === 'SUBMITTED' || status === 'REJECTED'
          );
        case 'REJECT':
          return selectedAssignmentStatuses.every(
            status => status === 'SUBMITTED' || status === 'APPROVED'
          );
        default:
          return false;
      }
    },
    onFiltered(results) {
      this.$emit('load-spinner', false);
      this.$store.dispatch(AssignmentsListingActions.UpdateAssignmentsList, {
        results,
        options: this.listingOptions
      });
    },
    onSearchInProgress(searchFlag) {
      this.$emit('load-spinner', searchFlag);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.assignments-actions {
  display: flex;
  background-color: $background-level-2;
  padding: 60px;

  .spacer {
    flex-grow: 1;
  }

  .listing-title {
    color: $text-color-dark;
    font-size: 21px;
    font-weight: bold;
    letter-spacing: -0.24px;
    line-height: 28px;
    padding-top: 10px;
  }

  .create-action-container {
    display: inline-block;
    text-decoration: none;
    color: $text-color-dark;
    padding: 0px 10px;
    white-space: nowrap;
  }

  .search {
    flex-basis: 60%;
  }
}
</style>
